import { WIN_BACK_PROMO, isWinBackPromoActive } from './winBackPromo'

/**
 * Carries a campaign coupon from a landing URL through signup and into checkout.
 *
 * The public offer page at /offer is reachable without an account, but Stripe checkout
 * is not: you cannot be charged without a customer, and a customer needs a signup. So
 * the coupon has to survive the walk from the ad click to the checkout call, which
 * happens several screens later and after a page navigation.
 *
 * localStorage rather than Redux for exactly that reason. It also survives the round
 * trip out to Stripe and back.
 *
 * Everything here is gated on the promo window being open. A link shared in October
 * must not still be handing a dead coupon to checkout, where Stripe would reject it and
 * the learner would be charged full price after being shown a discount.
 */
const KEY = 'maayot.offerCoupon'

/**
 * A campaign a landing URL is allowed to name, with enough of its terms to render the
 * offer wherever it has to be honoured. Signup is several screens from /offer and must
 * show the same price the ad promised, so the discount cannot stay behind on the
 * landing page.
 */
export type OfferCampaign = {
  code: string
  discountPercent: number
  durationMonths: number
  isActive: (now?: Date) => boolean
  /** Stripe products the coupon is limited to. Empty or missing means every plan. */
  productIds?: string[]
}

/** Coupons this build is willing to honour, and the window each one is live for. */
const KNOWN: OfferCampaign[] = [
  {
    code: WIN_BACK_PROMO.couponCode,
    discountPercent: WIN_BACK_PROMO.discountPercent,
    durationMonths: WIN_BACK_PROMO.durationMonths,
    isActive: isWinBackPromoActive,
  },
]

/**
 * Read a coupon off the URL and remember it. Accepts `?code=` or `?coupon=`.
 * Unknown or expired codes are ignored rather than stored, so a typo in an ad URL fails
 * closed instead of reaching Stripe.
 */
export function captureOfferCoupon(search: string = window.location.search, now: Date = new Date()): string | null {
  let raw: string | null = null
  try {
    const params = new URLSearchParams(search)
    raw = params.get('code') || params.get('coupon')
  } catch {
    return null
  }
  if (!raw) return null

  const code = raw.trim().toLowerCase()
  const known = KNOWN.find((k) => k.code.toLowerCase() === code)
  if (!known || !known.isActive(now)) return null

  try {
    localStorage.setItem(KEY, known.code)
  } catch {
    // Private browsing, or storage disabled. The offer still displays; it just will not
    // follow them into checkout. Better than throwing on a landing page.
  }
  return known.code
}

/**
 * The stored campaign, if there is one and it is still live. Carries the terms so a
 * screen can show the discounted price rather than only hand a code to Stripe.
 */
export function getStoredOfferCampaign(now: Date = new Date()): OfferCampaign | undefined {
  let stored: string | null = null
  try {
    stored = localStorage.getItem(KEY)
  } catch {
    return undefined
  }
  if (!stored) return getStoredPartnerCampaign(now)

  const known = KNOWN.find((k) => k.code === stored)
  if (!known || !known.isActive(now)) {
    clearOfferCoupon()
    return getStoredPartnerCampaign(now)
  }
  return known
}

/** The stored coupon code, if there is one and it is still live. */
export function getOfferCoupon(now: Date = new Date()): string | undefined {
  return getStoredOfferCampaign(now)?.code
}

/**
 * Discounted monthly price under the stored campaign, or null when there is no live
 * offer or no usable price. Mirrors the campaign modules' own pricing helpers: the
 * regular price always comes live from Stripe, never from a constant here.
 */
export function getOfferPricing(
  regularMonthlyPrice?: number | null,
  now: Date = new Date(),
  campaignOverride?: OfferCampaign | null,
): { campaign: OfferCampaign; regularMonthly: number; promoMonthly: number } | null {
  const campaign = campaignOverride === undefined ? getStoredOfferCampaign(now) : campaignOverride
  if (!campaign) return null
  if (!regularMonthlyPrice || isNaN(regularMonthlyPrice) || regularMonthlyPrice <= 0) return null
  return {
    campaign,
    regularMonthly: regularMonthlyPrice,
    promoMonthly: (regularMonthlyPrice * (100 - campaign.discountPercent)) / 100,
  }
}

export function clearOfferCoupon(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // nothing to do
  }
}

/*
 * Partner codes (e.g. a blogger's MBM102026) arrive on a /signup link:
 *   /signup?code=MBM102026&utm_source=...
 *
 * Unlike the campaigns above they are not listed in this build. Each partner gets its own
 * Stripe promotion code, so hard-coding them would mean a deploy per partner. Instead the
 * code is remembered on arrival, checked against Stripe once the learner has an account
 * (validate-coupon needs a login), and only then shown as a discount. An unknown or dead
 * code therefore still fails closed: nothing is displayed and nothing reaches checkout.
 */
const PARTNER_CODE_KEY = 'maayot.partnerCode'
const PARTNER_CAMPAIGN_KEY = 'maayot.partnerCampaign'
const PARTNER_CODE_PATTERN = /^[A-Za-z0-9_-]{3,40}$/
/** A validated partner discount is re-checked after this long, in case the code was switched off. */
const PARTNER_CAMPAIGN_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * Read `?code=` / `?coupon=` off a signup link. A known campaign code is handed to
 * `captureOfferCoupon` as before; anything else that looks like a code is kept as a
 * pending partner code for the plans step to validate.
 */
export function capturePartnerCode(search: string = window.location.search, now: Date = new Date()): string | null {
  let raw: string | null = null
  try {
    const params = new URLSearchParams(search)
    raw = params.get('code') || params.get('coupon')
  } catch {
    return null
  }
  const code = raw?.trim()
  if (!code) return null

  const campaign = captureOfferCoupon(search, now)
  if (campaign) return campaign
  if (KNOWN.some((k) => k.code.toLowerCase() === code.toLowerCase())) return null // a known campaign whose window is closed
  if (!PARTNER_CODE_PATTERN.test(code)) return null

  try {
    if (localStorage.getItem(PARTNER_CODE_KEY) !== code) localStorage.removeItem(PARTNER_CAMPAIGN_KEY)
    localStorage.setItem(PARTNER_CODE_KEY, code)
  } catch {
    return null
  }
  return code
}

/** The partner code remembered from the signup link, validated or not. */
export function getPendingPartnerCode(): string | undefined {
  try {
    return localStorage.getItem(PARTNER_CODE_KEY) || undefined
  } catch {
    return undefined
  }
}

/** The subset of the validate-coupon response this module reads. */
export type ValidatedCoupon = {
  percent_off?: number | null
  amount_off?: number | null
  duration?: string | null
  duration_in_months?: number | null
  interval_scope?: string | null
  applies_to_products?: string[] | null
}

/**
 * Turn a validate-coupon response into terms the plan cards can show, or null when the
 * discount cannot be shown honestly. The cards say "X/month for N months, then Y/month"
 * on monthly prices, so only percentage coupons with a fixed number of months (or a
 * single invoice) qualify. Fixed-amount coupons are skipped because they are set in one
 * currency and the cards may be showing another.
 */
export function partnerCampaignFromValidation(code: string, result: ValidatedCoupon | null | undefined): OfferCampaign | null {
  if (!result || typeof result !== 'object') return null
  const percent = Number(result.percent_off)
  if (!(percent > 0 && percent < 100)) return null
  if (result.interval_scope && result.interval_scope !== 'month') return null

  let months: number
  if (result.duration === 'repeating') months = Number(result.duration_in_months)
  else if (result.duration === 'once') months = 1
  else return null
  if (!(months > 0)) return null

  return {
    code,
    discountPercent: percent,
    durationMonths: months,
    isActive: () => true,
    productIds: Array.isArray(result.applies_to_products) ? result.applies_to_products : [],
  }
}

/**
 * What a validate-coupon call actually told us about a partner code.
 *
 * The distinction that matters is `rejected` vs `unavailable`. `validateCouponCode`
 * collapses every failure into a value — a 400 resolves to the server's message string,
 * but a timeout or an offline tab resolves to `undefined`, because there is no response
 * to read a message off. Treating that silence as a rejection would delete a perfectly
 * good partner code on one flaky request and quietly charge list price, so a result we
 * cannot read is left pending for the next load to re-check.
 */
export type PartnerValidation =
  | { status: 'applied'; campaign: OfferCampaign }
  | { status: 'rejected'; reason: string }
  | { status: 'unavailable' }

export function resolvePartnerValidation(code: string, result: unknown): PartnerValidation {
  // A string is the server's own rejection message: a real verdict.
  if (typeof result === 'string') {
    return result.trim() ? { status: 'rejected', reason: result } : { status: 'unavailable' }
  }
  // No body at all — transport failure, a 5xx, or a 200 we cannot read. Not a verdict.
  if (!result || typeof result !== 'object') return { status: 'unavailable' }

  const campaign = partnerCampaignFromValidation(code, result as ValidatedCoupon)
  // Stripe accepted the code but its terms are ones the cards cannot state honestly.
  // That will not change on a retry, so it is a rejection.
  return campaign ? { status: 'applied', campaign } : { status: 'rejected', reason: 'unsupported coupon terms' }
}

/** Remember a validated partner campaign so a reload of the plans step keeps the price. */
export function storePartnerCampaign(campaign: OfferCampaign, now: Date = new Date()): void {
  try {
    localStorage.setItem(
      PARTNER_CAMPAIGN_KEY,
      JSON.stringify({
        code: campaign.code,
        discountPercent: campaign.discountPercent,
        durationMonths: campaign.durationMonths,
        productIds: campaign.productIds || [],
        validatedAt: now.getTime(),
      }),
    )
  } catch {
    // Storage off: the discount still shows for this visit, it just is not remembered.
  }
}

function getStoredPartnerCampaign(now: Date = new Date()): OfferCampaign | undefined {
  try {
    const code = localStorage.getItem(PARTNER_CODE_KEY)
    const raw = localStorage.getItem(PARTNER_CAMPAIGN_KEY)
    if (!code || !raw) return undefined
    const saved = JSON.parse(raw)
    if (saved?.code !== code || !(now.getTime() - Number(saved.validatedAt) < PARTNER_CAMPAIGN_TTL_MS)) {
      localStorage.removeItem(PARTNER_CAMPAIGN_KEY)
      return undefined
    }
    return {
      code: saved.code,
      discountPercent: Number(saved.discountPercent),
      durationMonths: Number(saved.durationMonths),
      productIds: Array.isArray(saved.productIds) ? saved.productIds : [],
      isActive: () => true,
    }
  } catch {
    return undefined
  }
}

/** Whether a campaign's coupon can be applied to a plan (Stripe product scoping). */
export function campaignAppliesToProduct(campaign: OfferCampaign | null | undefined, productId?: string): boolean {
  if (!campaign) return false
  if (!campaign.productIds || campaign.productIds.length === 0) return true
  return Boolean(productId && campaign.productIds.includes(productId))
}

export function clearPartnerCode(): void {
  try {
    localStorage.removeItem(PARTNER_CODE_KEY)
    localStorage.removeItem(PARTNER_CAMPAIGN_KEY)
  } catch {
    // nothing to do
  }
}
